import {
	createHook,
	createStore,
	createContainer,
	createActionsHook,
	createSelector,
} from 'react-sweet-state';

import { actions } from './actions';
import type { State, UseCollapsedParams } from './types';

const store = createStore<State, typeof actions>({
	initialState: {
		columns: [],
		widths: [],
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onWidthsChange: () => {},
		stickyOffset: {
			vertical: 0,
			horizontal: 0,
		},
		rows: [],
	},
	actions,
});

export const useGrid = createHook(store);

export const useColumnConfig = createHook(store, {
	selector: (state, col: number) => state.columns[col],
});

export const useRowConfig = createHook(store, {
	selector: (state, col: number) => state.rows[col],
});

/** Returns whether the column or cell is collapsed. */
export const useCollapsed = createHook(store, {
	selector: createSelector(
		[
			(state, { column, colSpan, preview }: UseCollapsedParams) =>
				((preview ? state.preview : undefined) ?? state.widths)
					.slice(column, column + colSpan)
					.reduce(
						(acc: number, width = Infinity) =>
							Number.isFinite(acc) && Number.isFinite(width) ? acc + width : Infinity,
						0,
					),
			(_, { threshold }) => threshold,
		],
		(width, threshold) => width <= threshold,
	),
});

export const useColumnWidth = createHook(store, {
	selector: (state, { column, preview }: { column: number; preview: boolean }) =>
		((preview ? state.preview : undefined) ?? state.widths).at(column),
});

export const useColumnWidths = createHook(store, {
	selector: (state, { preview }: { preview: boolean }) =>
		(preview ? state.preview : undefined) ?? state.widths,
});

export const useColumnStickyOffset = createHook(store, {
	selector: (state, { column, preview }: { column: number; preview: boolean }) =>
		state.columns[column].sticky
			? ((preview ? state.preview : undefined) ?? state.widths)
					.slice(0, column)
					.reduce((offset = 0, width) => offset + width, state.stickyOffset.horizontal)
			: undefined,
});

export const useRowStickyOffset = createHook(store, {
	selector: (state, { row, virtualized }: { row: number; virtualized: number[] }) => {
		const sticky = virtualized.filter((index) => state.rows[index].sticky);
		if (!sticky.includes(row)) {
			return undefined;
		}

		return sticky
			.slice(0, sticky.indexOf(row))
			.map((index) => state.rows[index])
			.reduce((acc, { height }) => acc + height, state.stickyOffset.vertical);
	},
});

export const useIsResizing = createHook(store, {
	selector: (state) => state.preview !== undefined,
});
export const useColumns = createHook(store, {
	selector: (state) => ({ columns: state.columns, widths: state.widths }),
});

export const useGridActions = createActionsHook(store);

export const GridProvider = createContainer(store, {
	onInit:
		() =>
		({ setState }, state: State) =>
			setState(state),
	onUpdate:
		() =>
		({ setState }, state: State) =>
			setState(state),
});

export type { Resizer } from './types';
